import Backdrop from 'components/backdrop';
import BlockContent from 'components/block-content';
import ContentBlock from 'components/content-block';
import CTABlock from 'components/cta-block';
import Decoration from 'components/decoration';
import ImageSlider from 'components/image-slider';
import Inclusions from 'components/inclusions';
import LocationMap from 'components/location-map';
import Locations from 'components/locations';
import { Sizes } from 'components/sizes';
import {
  Button,
  Heading,
  Image,
  ImageGrid,
  Stack,
  StaggerGrid,
  Text,
  TextBlock,
} from 'components/ui';
import Link from 'next/link';
import { FC } from 'react';
import { SETTINGS } from 'settings/config';
import { SharedData } from 'utils/queries';
import { getFlourishPosition, urlForImage } from 'utils/sanity';

interface ModuleRenderProps {
  modules: Sanity.Schema.Modules;
  sharedData: SharedData;
}

const ModuleRender: FC<ModuleRenderProps> = ({ modules, sharedData }) => {
  return (
    <>
      {modules.map((module) => {
        switch (module._type) {
          // CONTENT BLOCK
          case 'contentBlock':
            const imageUrls: any = module.images?.map((image) => ({
              src: urlForImage(image).width(1024).url() || '',
              alt: '',
            }));

            return (
              <ContentBlock
                key={module._key}
                backgroundColor={module.background}
                flip={module.flip}
                {...getFlourishPosition(module.flourish)}
              >
                <Stack space={{ initial: 2, large: 4, xLarge: 6 }}>
                  <Heading level='h2'>{module.title}</Heading>
                  <BlockContent blocks={module.text} />
                  {module.cta && module.cta.link && module.cta.text && (
                    <Link href={module.cta.link}>
                      <Button as='a'>{module.cta.text}</Button>
                    </Link>
                  )}
                </Stack>
                {module.images && module.images.length < 4 && (
                  <ImageGrid>
                    {module.images.map((image, index) => {
                      const imageUrl = imageUrls[index];

                      if (!imageUrl.src) return null;

                      return (
                        <Image
                          key={image._key}
                          src={imageUrl.src}
                          alt={image.alt}
                          layout='fill'
                          objectFit='cover'
                        />
                      );
                    })}
                  </ImageGrid>
                )}
                {module.images && module.images.length > 3 && (
                  <ImageSlider images={imageUrls} />
                )}
              </ContentBlock>
            );
          // CONTENT GRID
          case 'contentGrid':
            return (
              <Backdrop key={module._key}>
                <Stack space={{ initial: 2, large: 4, xLarge: 6 }}>
                  {module.title && <Heading level='h2'>{module.title}</Heading>}
                  <StaggerGrid>
                    {module.items?.map((item) => (
                      <TextBlock key={item._key}>
                        <Stack space={{ initial: 1, medium: 3 }}>
                          {item.image && (
                            <Image
                              alt={item.image.alt || item.title}
                              src={urlForImage(item.image).url() || ''}
                              width={1024}
                              height={1024}
                              objectFit='cover'
                            />
                          )}
                          <Heading level='h3' variant='sans-serif'>
                            {item.title}
                          </Heading>
                          <Text>{item.text}</Text>
                          {item.cta && item.cta.link && item.cta.text && (
                            <Link href={item.cta.link} passHref>
                              <Button as='a'>{item.cta.text}</Button>
                            </Link>
                          )}
                        </Stack>
                      </TextBlock>
                    ))}
                  </StaggerGrid>
                </Stack>
              </Backdrop>
            );
          // FIXED
          case 'fixedModule':
            if (module.type === 'decoration') {
              return (
                <Backdrop key={module._key}>
                  <Decoration />
                </Backdrop>
              );
            }
            if (module.type === 'locations') {
              return (
                <LocationMap
                  key={module._key}
                  locations={sharedData.locations}
                />
              );
            }
            if (module.type === 'cta') {
              return (
                <CTABlock
                  key={module._key}
                  content={[
                    {
                      title: 'Learn how to keep more of your\u00a0money',
                      linkCTA: 'Download profit calculator',
                      link: '/Salon-Studios-Profit-Calculator.xlsx',
                    },
                    {
                      title: 'Ready to make your next move?',
                      linkCTA: 'Get in touch',
                      link: SETTINGS.routes.contact.slug,
                    },
                  ]}
                />
              );
            }
            if (module.type === 'ctaLocations') {
              return (
                <Locations key={module._key} locations={sharedData.locations} />
              );
            }
            return null;
          // INCLUSIONS
          case 'inclusions':
            return <Inclusions key={module._key} data={module} />;
          // SIZES
          case 'sizes':
            return <Sizes key={module._key} module={module} />;
          default:
            return null;
        }
      })}
    </>
  );
};

export default ModuleRender;
